import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import "./buildingCareers.css";
import { featuredPlacements as ITEMS } from "../../data/home/featuredPlacements";
import { careersData } from "../../data/home";
import { mediaUrl } from "../../services/assetUrl";

const BASE = process.env.PUBLIC_URL || "";
const ROTATE_MS = 4200;

// "Building Careers, Shaping Futures" — a cinematic, image-led placements stage.
// Layered overlap: a giant faded company wordmark sits behind (left), the
// student photo rises and overlaps the center seam (right), and a glass
// name-card bridges them. Each set slides in (company from the left) while the
// photo parallax-floats up from below; on exit they part in opposite
// directions. A rail of all 5 student faces below = overview + progress.
const BuildingCareers = ({ data, featured }) => {
  const items = (featured && featured.length) ? featured : ITEMS;
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hidden, setHidden] = useState(false);
  const n = items.length;
  const item = items[active] || items[0];

  const go = useCallback((i) => setActive(((i % n) + n) % n), [n]);

  // Preload every photo + logo once, so auto-advance / clicking a face /
  // returning to the page never stalls while a big image decodes.
  useEffect(() => {
    items.forEach((s) => {
      const p = new Image();
      p.src = mediaUrl(s.photo);
      const l = new Image();
      l.src = mediaUrl(s.logo);
    });
  }, []);

  // Pause cleanly while the tab is hidden (avoids throttled-timer stalls on return).
  useEffect(() => {
    const onVis = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Safety net: if hover left `paused` stuck (e.g. you hovered then scrolled
  // away, so `mouseleave` never fired), the next scroll resumes autoplay.
  useEffect(() => {
    if (!paused) return undefined;
    const resume = () => setPaused(false);
    window.addEventListener("scroll", resume, { passive: true, once: true });
    return () => window.removeEventListener("scroll", resume);
  }, [paused]);

  // Advance on a timer that resets whenever the slide / pause / visibility changes.
  useEffect(() => {
    if (reduce || paused || hidden) return undefined;
    const id = setTimeout(() => setActive((a) => (a + 1) % n), ROTATE_MS);
    return () => clearTimeout(id);
  }, [active, paused, hidden, reduce, n]);

  // Title: split out the highlighted word for the orange accent.
  const { badge, heading, highlight, subtitle } = data || careersData;
  const parts = highlight && heading.includes(highlight) ? heading.split(highlight) : null;

  // ---- motion variants (different springs per layer → parallax) ----
  const companyV = reduce
    ? { initial: { opacity: 0 }, animate: { opacity: 1, transition: { duration: 0.3 } }, exit: { opacity: 0, transition: { duration: 0.2 } } }
    : {
        initial: { x: -80, opacity: 0 },
        animate: { x: 0, opacity: 1, transition: { type: "spring", stiffness: 90, damping: 18, opacity: { duration: 0.55 } } },
        exit: { x: -64, opacity: 0, transition: { duration: 0.45, ease: "easeIn" } },
      };
  const photoV = reduce
    ? { initial: { opacity: 0 }, animate: { opacity: 1, transition: { duration: 0.3 } }, exit: { opacity: 0, transition: { duration: 0.2 } } }
    : {
        initial: { y: 96, opacity: 0, scale: 0.94 },
        animate: { y: 0, opacity: 1, scale: 1, transition: { type: "spring", stiffness: 66, damping: 16, opacity: { duration: 0.5 } } },
        exit: { y: 56, opacity: 0, scale: 0.97, transition: { duration: 0.45, ease: "easeIn" } },
      };
  const cardV = reduce
    ? { initial: { opacity: 0 }, animate: { opacity: 1, transition: { duration: 0.3 } }, exit: { opacity: 0, transition: { duration: 0.2 } } }
    : {
        initial: { y: 44, opacity: 0 },
        animate: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 120, damping: 18, delay: 0.14, staggerChildren: 0.06, delayChildren: 0.2 } },
        exit: { y: 28, opacity: 0, transition: { duration: 0.3, ease: "easeIn" } },
      };
  const lineV = reduce
    ? { initial: { opacity: 1 }, animate: { opacity: 1 } }
    : { initial: { y: 14, opacity: 0 }, animate: { y: 0, opacity: 1 } };

  return (
    <section className="bcx" aria-labelledby="bcx-title">
      {/* decor */}
      <span className="bcx__grid" aria-hidden="true" />
      <span className="bcx__orb bcx__orb--a" aria-hidden="true" />
      <span className="bcx__orb bcx__orb--b" aria-hidden="true" />
      <span className="bcx__watermark" aria-hidden="true">PLACEMENTS</span>

      <div className="bcx__head container">
        <span className="bcx__badge">
          {badge}
          <span className="bcx__badge-dot" aria-hidden="true" />
        </span>
        <h2 id="bcx-title" className="bcx__title">
          {parts ? (<>{parts[0]}<span>{highlight}</span>{parts[1]}</>) : heading}
        </h2>
        <p className="bcx__subtitle">{subtitle}</p>
      </div>

      {/* ---- cinematic stage ---- */}
      <div
        className="bcx__stage"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        aria-live="polite"
      >
        <div className="bcx__stageInner">
          {/* company (faded wordmark + crisp chip), slides in from left */}
          <AnimatePresence>
            <motion.div className="bcx__company" key={`co-${active}`} variants={companyV} initial="initial" animate="animate" exit="exit">
              <img className="bcx__ghost" src={mediaUrl(item.logo)} alt="" aria-hidden="true" />
              <div className="bcx__coLabel">
                <span className="bcx__eyebrow">Placed at</span>
                <span className="bcx__chip">
                  <img src={mediaUrl(item.logo)} alt={item.company} loading="lazy" />
                </span>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* student photo, parallax-rises from below */}
          <AnimatePresence>
            <motion.div
              className={`bcx__photo${item.portrait ? " is-portrait" : ""}`}
              key={`ph-${active}`}
              variants={photoV}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <img src={mediaUrl(item.photo)} alt={item.name} loading="eager" decoding="async" />
              <span className="bcx__photo-glow" aria-hidden="true" />
            </motion.div>
          </AnimatePresence>

          {/* glass name-card bridging the seam */}
          <AnimatePresence>
            <motion.div className="bcx__card" key={`cd-${active}`} variants={cardV} initial="initial" animate="animate" exit="exit">
              <motion.span className="bcx__placed" variants={lineV}>
                <span className="bcx__placed-dot" aria-hidden="true" />Placed
              </motion.span>
              <motion.h3 className="bcx__name" variants={lineV}>{item.name}</motion.h3>
              <motion.p className="bcx__branch" variants={lineV}>{item.branch}</motion.p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* ---- footer: face rail (overview + progress) + view-all on one line ---- */}
      <div className="bcx__footer">
        <div className="bcx__rail" role="tablist" aria-label="Placed students">
          {items.map((s, i) => (
            <button
              key={s.photo}
              type="button"
              role="tab"
              aria-selected={i === active}
              aria-label={`${s.name} — placed at ${s.company}`}
              className={`bcx__face${i === active ? " is-active" : ""}`}
              onClick={() => go(i)}
            >
              <img src={mediaUrl(s.photo)} alt="" loading="lazy" />
            </button>
          ))}
          {!reduce && (
            <span
              className="bcx__progress"
              key={active}
              style={{ animationDuration: `${ROTATE_MS}ms`, animationPlayState: paused ? "paused" : "running" }}
              aria-hidden="true"
            />
          )}
        </div>

        <Link to="/placements" className="bcx__cta">
          <span className="bcx__cta-label">View All Placements</span>
          <span className="bcx__cta-arrow" aria-hidden="true">→</span>
        </Link>
      </div>
    </section>
  );
};

export default BuildingCareers;
